import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Stethoscope, ArrowRight, Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import './MarketingNav.css';

const MarketingNav = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const closeMenu = () => setIsMenuOpen(false);

  // Close on Escape
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  // Scroll lock
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  // Close on route change
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  return (
    <nav className="landing-nav">
      <div className="landing-nav-container">
        <div className="landing-nav-content">
          <Link to="/" className="landing-logo">
            <div className="landing-logo-icon"><Stethoscope /></div>
            <span className="landing-logo-text">
              Amplify <span className="landing-logo-sub">by CareTracker</span>
            </span>
          </Link>

          {/* Desktop links (hidden on mobile via CSS) */}
          <div className="landing-nav-links">
            <Link to="/features" className={`landing-nav-link ${isActive('/features') ? 'active' : ''}`}>Features</Link>
            <Link to="/about" className={`landing-nav-link ${isActive('/about') ? 'active' : ''}`}>About</Link>
            <Link to="/pricing" className={`landing-nav-link ${isActive('/pricing') ? 'active' : ''}`}>Pricing</Link>
            <Link to="/contact" className={`landing-nav-link ${isActive('/contact') ? 'active' : ''}`}>Contact</Link>
            <Link to="/login" className={`landing-nav-link ${isActive('/login') ? 'active' : ''}`}>Sign In</Link>
            <Link to="/contact" className="landing-cta-button">Contact Sales <ArrowRight size={16} /></Link>
          </div>

          {/* Hamburger button (mobile only, hidden on desktop via CSS) */}
          <button
            className="landing-nav-hamburger"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              className="landing-mobile-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMenu}
            />
            <motion.div
              className="landing-mobile-menu"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              <Link to="/features" className="landing-mobile-link" onClick={closeMenu}>Features</Link>
              <Link to="/about" className="landing-mobile-link" onClick={closeMenu}>About</Link>
              <Link to="/pricing" className="landing-mobile-link" onClick={closeMenu}>Pricing</Link>
              <Link to="/contact" className="landing-mobile-link" onClick={closeMenu}>Contact</Link>
              <div className="landing-mobile-divider" />
              <Link to="/login" className="landing-mobile-signin" onClick={closeMenu}>Sign In</Link>
              <Link to="/contact" className="landing-mobile-cta" onClick={closeMenu}>
                Contact Sales <ArrowRight size={16} />
              </Link>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default MarketingNav;
